import './App.css';
import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom'; // Still needed for query param updates
import ForceGraph from './components/ForceGraph';
import SettingsMenu from './components/SettingsMenu';
import NodeInfoPopup from './components/NodeInfoPopup';
import { useInitializeAppState } from './utils/initialStates';

function App({ fileOptions }) {
  const {
    graphState,
    selectedFile: initialSelectedFile,
    disableInitialSwitch: initialDisableSwitch,
    nodeSize: initialNodeSize,
    isDarkMode: initialDarkMode,
  } = useInitializeAppState();

  const [data, setData] = useState(graphState);
  const [selectedFile, setSelectedFile] = useState(initialSelectedFile);
  const [disableInitialSwitch, setDisableInitialSwitch] = useState(initialDisableSwitch);
  const [nodeSize, setNodeSize] = useState(initialNodeSize);
  const [selectedNode, setSelectedNode] = useState(null);
  const [isDarkMode, setIsDarkMode] = useState(initialDarkMode);
  const [colorKey, setColorKey] = useState('default');
  const [selectedTags, setSelectedTags] = useState([]);
  const [showAllNodes, setShowAllNodes] = useState(false); // State to control showAllNodes

  const [searchParams, setSearchParams] = useSearchParams();

  // Update query parameters when settings change
  useEffect(() => {
    setSearchParams({
      darkMode: isDarkMode,
      initialSwitch: disableInitialSwitch,
      nodeSize,
      selectedFile,
    });
  }, [isDarkMode, disableInitialSwitch, nodeSize, selectedFile, setSearchParams]);

  // Update colorKey when data changes
  useEffect(() => {
    if (data.nodes.length > 0) {
      const firstNodeColor = data.nodes[0].color;
      if (!firstNodeColor) {
        setColorKey('default'); // Set to default if color doesn't exist
      } else if (typeof firstNodeColor === 'object') {
        setColorKey(Object.keys(firstNodeColor)[0]); // Set to the first key
      } else {
        setColorKey('default'); // Set to default if it's a string
      }
    }
  }, [data]);

  // Filter nodes based on selected tags
  const filteredData = {
    nodes: data.nodes.filter(node => {
      if (selectedTags.length === 0) return true; // Show all nodes if no tags are selected
      return node.tags && selectedTags.every(tag => node.tags.includes(tag));
    }),
    links: data.links.filter(link => {
      // Ensure both source and target nodes are in the filtered nodes
      const sourceNode = data.nodes.find(node => node.id === link.source);
      const targetNode = data.nodes.find(node => node.id === link.target);
      return sourceNode && targetNode;
    }),
  };

  // Function to get other comment nodes with the same post_id
  const getOtherCommentNodes = (selectedNode) => {
    if (!selectedNode || !selectedNode.post_id) return [];
    return data.nodes.filter(node => node.post_id === selectedNode.post_id && node.id !== selectedNode.id);
  };

  return (
    <div className={`App ${isDarkMode ? 'dark-mode' : 'light-mode'}`}>
      <ForceGraph data={filteredData} setSelectedNode={setSelectedNode} nodeSize={nodeSize} colorKey={colorKey} />
      <SettingsMenu
        data={data}
        setData={setData}
        selectedFile={selectedFile}
        setSelectedFile={setSelectedFile}
        disableInitialSwitch={disableInitialSwitch}
        setDisableInitialSwitch={setDisableInitialSwitch}
        nodeSize={nodeSize}
        setNodeSize={setNodeSize}
        isDarkMode={isDarkMode}
        setIsDarkMode={setIsDarkMode}
        colorKey={colorKey}
        setColorKey={setColorKey}
        selectedTags={selectedTags}
        setSelectedTags={setSelectedTags}
        fileOptions={fileOptions}
        setShowAllNodes={setShowAllNodes}
      />
      <NodeInfoPopup
        selectedNode={selectedNode}
        closePopup={() => {
          setSelectedNode(null);
          setShowAllNodes(false); // Close all nodes popup
        }}
        isDarkMode={isDarkMode}
        colorKey={colorKey}
        otherCommentNodes={showAllNodes ? data.nodes : getOtherCommentNodes(selectedNode)} // Show all nodes or other comments
        showAllNodes={showAllNodes} // Pass showAllNodes state
      />
    </div>
  );
}

export default App;
